import { useEffect, useState } from 'react'
import { useAppSelector } from '../hooks/redux'
import { Issue } from '@/types'
import IssueDetailModal from '../components/IssueDetailModal'
import { useToast } from '../components/ToastContainer'
import {
  ArchiveBoxArrowDownIcon,
  EyeIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import * as issueApi from '../services/api/issue'

const ArchivedIssues = () => {
  const { currentProject } = useAppSelector(state => state.project)
  const { user } = useAppSelector(state => state.auth)
  const { showSuccess, showError } = useToast()
  
  const [issues, setIssues] = useState<Issue[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'


  useEffect(() => {
    if (currentProject?.id) {
      loadArchivedIssues()
    }
  }, [currentProject?.id])

  const loadArchivedIssues = async () => {
    if (!currentProject?.id) return
    
    try {
      setLoading(true)
      const response = await issueApi.getArchivedIssues(currentProject.id)
      if (response.success) {
        setIssues(response.data?.issues || [])
      }
    } catch (error) {
      console.error('載入封存議題失敗:', error)
      showError('載入失敗', '無法載入封存議題')
    } finally {
      setLoading(false)
    }
  }

  const handleUnarchive = async (issue: Issue) => {
    try {
      await issueApi.archiveIssue(issue.id, false)
      showSuccess('已取消封存', `議題「${issue.title}」已恢復到看板`)
      await loadArchivedIssues()
    } catch (error) {
      console.error('取消封存失敗:', error)
      showError('操作失敗', '無法取消封存議題')
    }
  }

  const handleDelete = async (issue: Issue) => {
    if (!confirm(`確定要刪除議題「${issue.title}」嗎？刪除後將移至回收桶。`)) {
      return
    }

    try {
      await issueApi.deleteIssue(issue.id)
      showSuccess('已移至回收桶', `議題「${issue.title}」已移至回收桶`)
      await loadArchivedIssues()
    } catch (error) {
      console.error('刪除失敗:', error)
      showError('操作失敗', '無法刪除議題')
    }
  }

  const handleIssueClick = (issue: Issue) => {
    setSelectedIssue(issue)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedIssue(null)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* 統計資訊 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold text-orange-600">{issues.length}</div>
            <div className="text-gray-600 text-sm">封存議題</div>
          </div>
          <ArchiveBoxArrowDownIcon className="w-8 h-8 text-orange-500" />
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-gray-600">
            {currentProject?.name} - 已封存的議題
          </p>
          <div className="text-sm text-gray-500">
            共 {issues.length} 個封存議題
          </div>
        </div>

        {/* Issues Grid */}
        {issues.length === 0 ? (
          <div className="text-center py-12">
            <ArchiveBoxArrowDownIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">沒有封存議題</h3>
            <p className="text-gray-500">目前沒有任何封存的議題</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {issues.map((issue) => (
              <div
                key={issue.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer relative group"
              >
                {/* Archive Indicator */}
                <div className="absolute top-2 right-2 opacity-60">
                  <ArchiveBoxArrowDownIcon className="w-4 h-4 text-gray-500" />
                </div>

                {/* Issue Content */}
                <div onClick={() => handleIssueClick(issue)} className="pr-6">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">{issue.key}</span>
                  </div>
                  
                  <h4 className="text-sm font-medium text-gray-700 mb-2 line-clamp-2">
                    {issue.title}
                  </h4>
                  
                  {issue.description && (
                    <div 
                      className="text-xs text-gray-500 mb-3 line-clamp-2"
                      dangerouslySetInnerHTML={{ 
                        __html: issue.description
                          .replace(/<img[^>]*>/g, '[圖片]')
                          .replace(/<[^>]*>/g, '')
                          .substring(0, 100)
                      }}
                    />
                  )}

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-400">
                      封存於 {new Date(issue.updatedAt).toLocaleDateString()}
                    </div>
                    {issue.assignee && (
                      <div className="flex items-center">
                        {issue.assignee.avatar ? (
                          <img
                            src={issue.assignee.avatar}
                            alt={issue.assignee.name}
                            className="w-5 h-5 rounded-full"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-600">
                              {issue.assignee.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {isAdmin && (
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleIssueClick(issue)
                      }}
                      className="p-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded transition-colors"
                      title="檢視議題"
                    >
                      <EyeIcon className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleUnarchive(issue)
                      }}
                      className="p-1.5 bg-green-50 hover:bg-green-100 text-green-600 rounded transition-colors"
                      title="取消封存"
                    >
                      <ArchiveBoxArrowDownIcon className="w-3 h-3 transform rotate-180" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(issue)
                      }}
                      className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded transition-colors"
                      title="移至回收桶"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Issue Detail Modal */}
        {selectedIssue && (
          <IssueDetailModal
            issue={selectedIssue}
            isOpen={isModalOpen}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </div>
  )
}

export default ArchivedIssues
